import React, { useState, useEffect, useMemo } from "react";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Clock, LogIn, LogOut, PauseCircle, PlayCircle, Edit2, Calendar, CalendarDays, CalendarRange } from "lucide-react";
import moment from "moment";
import "moment/locale/ca";
moment.locale("ca");

const TYPE_LABELS = { entry: "Entrada", exit: "Sortida", pause_start: "Inici pausa", pause_end: "Fi pausa" };
const TYPE_ICONS = { entry: LogIn, exit: LogOut, pause_start: PauseCircle, pause_end: PlayCircle };
const TYPE_COLORS = { entry: "text-emerald-600 bg-emerald-50", exit: "text-rose-600 bg-rose-50", pause_start: "text-amber-600 bg-amber-50", pause_end: "text-blue-600 bg-blue-50" };

function calcWorkedMinutes(records) {
  const sorted = [...records].filter(r => !r.is_deleted).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  let total = 0, entryTime = null, pauseTime = null;
  for (const r of sorted) {
    if (r.type === "entry") { entryTime = new Date(r.timestamp); pauseTime = null; }
    else if (r.type === "pause_start" && entryTime && !pauseTime) pauseTime = new Date(r.timestamp);
    else if (r.type === "pause_end" && pauseTime) pauseTime = null;
    else if (r.type === "exit" && entryTime) {
      let workTime = new Date(r.timestamp) - entryTime;
      total += workTime / 60000;
      entryTime = null;
      pauseTime = null;
    }
  }
  if (entryTime) total += (new Date() - entryTime) / 60000;
  return Math.max(0, Math.round(total));
}

function calcPauseMinutes(records) {
  const sorted = [...records].filter(r => !r.is_deleted).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  let total = 0, pauseStart = null;
  for (const r of sorted) {
    if (r.type === "pause_start") pauseStart = new Date(r.timestamp);
    else if (r.type === "pause_end" && pauseStart) {
      total += (new Date(r.timestamp) - pauseStart) / 60000;
      pauseStart = null;
    }
  }
  if (pauseStart) total += (new Date() - pauseStart) / 60000;
  return Math.max(0, Math.round(total));
}

function minutesToHHMM(mins) {
  if (!mins && mins !== 0) return "0h 00m";
  return `${Math.floor(mins / 60)}h ${(mins % 60).toString().padStart(2, "0")}m`;
}

function DayRecordsList({ dayRecs, date }) {
  const worked = calcWorkedMinutes(dayRecs);
  const paused = calcPauseMinutes(dayRecs);
  const activeRecs = dayRecs.filter(r => !r.is_deleted);

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" data-testid={`day-records-${date}`}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50">
        <span className="font-semibold text-gray-800 text-sm capitalize">
          {moment(date).format("dddd, D MMMM")}
        </span>
        <div className="flex items-center gap-2">
          {paused > 0 && (
            <span className="text-[10px] text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
              Pausa: {minutesToHHMM(paused)}
            </span>
          )}
          {worked > 0 && (
            <span className="text-xs font-bold text-[#7B2D3F] bg-[#7B2D3F]/10 px-2 py-0.5 rounded-full">
              {minutesToHHMM(worked)}
            </span>
          )}
        </div>
      </div>
      <div className="divide-y divide-gray-50">
        {[...dayRecs].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)).map(r => {
          const Icon = TYPE_ICONS[r.type] || Clock;
          const colorClass = TYPE_COLORS[r.type] || "text-gray-500 bg-gray-50";
          return (
            <div key={r.id || r.time_record_id} className={`flex items-center gap-3 px-4 py-2.5 ${r.is_deleted ? "opacity-40" : ""}`}>
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                <Icon className="w-3.5 h-3.5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-800">{TYPE_LABELS[r.type]}</span>
                  {r.is_deleted && <Badge variant="outline" className="text-[10px] text-red-400 border-red-200 py-0">eliminat</Badge>}
                  {r.last_edited_by && !r.is_deleted && <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-200 py-0 flex items-center gap-0.5"><Edit2 className="w-2.5 h-2.5" />editat</Badge>}
                  {r.source === "manual" && !r.last_edited_by && <Badge variant="outline" className="text-[10px] text-blue-500 border-blue-200 py-0">manual</Badge>}
                  {r.source === "nfc" && <Badge variant="outline" className="text-[10px] text-purple-500 border-purple-200 py-0">NFC</Badge>}
                </div>
                {r.edit_reason && <p className="text-[10px] text-gray-400 truncate">Motiu: {r.edit_reason}</p>}
              </div>
              <span className="text-sm font-mono font-semibold text-gray-700 flex-shrink-0">
                {moment(r.timestamp).format("HH:mm")}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function WorkerTimeHistory({ isAdmin }) {
  const { currentVenue, user } = useVenue();
  const [members, setMembers] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [allRecords, setAllRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState("day");
  const [currentDate, setCurrentDate] = useState(moment());

  const viewEmail = isAdmin ? selectedEmail : user?.email;

  useEffect(() => {
    if (!currentVenue) return;
    if (isAdmin) {
      hosys.entities.VenueMember.filter({ venue_id: currentVenue.id, active: true })
        .then(m => {
          setMembers(m);
          if (!selectedEmail && m.length > 0) setSelectedEmail(m[0].user_email);
        });
    } else {
      setSelectedEmail(user?.email);
    }
  }, [currentVenue, isAdmin]);

  useEffect(() => {
    if (!currentVenue || !viewEmail) return;
    loadRecords();
  }, [currentVenue, viewEmail, currentDate.format("YYYY-MM")]);

  const loadRecords = async () => {
    setLoading(true);
    try {
      const monthStart = currentDate.clone().startOf("month").format("YYYY-MM-DD");
      const monthEnd = currentDate.clone().endOf("month").format("YYYY-MM-DD");
      const all = await hosys.entities.TimeRecord.filter({ venue_id: currentVenue.id, user_email: viewEmail });
      const filtered = all.filter(r => r.date >= monthStart && r.date <= monthEnd);
      setAllRecords(filtered.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)));
    } catch (err) {
      console.error("Error loading records:", err);
    }
    setLoading(false);
  };

  // Filtered records based on period
  const filteredRecords = useMemo(() => {
    if (period === "day") {
      const day = currentDate.format("YYYY-MM-DD");
      return allRecords.filter(r => r.date === day);
    } else if (period === "week") {
      const weekStart = currentDate.clone().startOf("isoWeek").format("YYYY-MM-DD");
      const weekEnd = currentDate.clone().endOf("isoWeek").format("YYYY-MM-DD");
      return allRecords.filter(r => r.date >= weekStart && r.date <= weekEnd);
    }
    return allRecords;
  }, [allRecords, period, currentDate.format("YYYY-MM-DD")]);

  // Group by date
  const byDate = useMemo(() => {
    const grouped = {};
    filteredRecords.forEach(r => {
      if (!grouped[r.date]) grouped[r.date] = [];
      grouped[r.date].push(r);
    });
    return grouped;
  }, [filteredRecords]);

  const totalMins = Object.values(byDate).reduce((acc, recs) => acc + calcWorkedMinutes(recs), 0);
  const totalPause = Object.values(byDate).reduce((acc, recs) => acc + calcPauseMinutes(recs), 0);
  const workDays = Object.keys(byDate).filter(d => calcWorkedMinutes(byDate[d]) > 0).length;

  // Summaries for the cards
  const todaySummary = useMemo(() => {
    const today = moment().format("YYYY-MM-DD");
    const todayRecs = allRecords.filter(r => r.date === today);
    return { mins: calcWorkedMinutes(todayRecs), pause: calcPauseMinutes(todayRecs), records: todayRecs.length };
  }, [allRecords]);

  const weekSummary = useMemo(() => {
    const weekStart = moment().startOf("isoWeek").format("YYYY-MM-DD");
    const weekEnd = moment().endOf("isoWeek").format("YYYY-MM-DD");
    const weekRecs = allRecords.filter(r => r.date >= weekStart && r.date <= weekEnd);
    const byDay = {};
    weekRecs.forEach(r => { if (!byDay[r.date]) byDay[r.date] = []; byDay[r.date].push(r); });
    const mins = Object.values(byDay).reduce((acc, recs) => acc + calcWorkedMinutes(recs), 0);
    const days = Object.keys(byDay).filter(d => calcWorkedMinutes(byDay[d]) > 0).length;
    return { mins, days };
  }, [allRecords]);

  const monthSummary = useMemo(() => {
    const byDay = {};
    allRecords.forEach(r => { if (!byDay[r.date]) byDay[r.date] = []; byDay[r.date].push(r); });
    const mins = Object.values(byDay).reduce((acc, recs) => acc + calcWorkedMinutes(recs), 0);
    const days = Object.keys(byDay).filter(d => calcWorkedMinutes(byDay[d]) > 0).length;
    return { mins, days };
  }, [allRecords]);

  // Navigation
  const goBack = () => {
    if (period === "day") setCurrentDate(d => d.clone().subtract(1, "day"));
    else if (period === "week") setCurrentDate(d => d.clone().subtract(1, "week"));
    else setCurrentDate(d => d.clone().subtract(1, "month"));
  };

  const goForward = () => {
    const next = period === "day" ? currentDate.clone().add(1, "day")
      : period === "week" ? currentDate.clone().add(1, "week")
      : currentDate.clone().add(1, "month");
    if (next.isAfter(moment())) return;
    setCurrentDate(next);
  };

  const goToday = () => setCurrentDate(moment());

  const periodLabel = period === "day" 
    ? currentDate.format("dddd, D MMMM YYYY")
    : period === "week"
    ? `${currentDate.clone().startOf("isoWeek").format("D MMM")} - ${currentDate.clone().endOf("isoWeek").format("D MMM YYYY")}`
    : currentDate.format("MMMM YYYY");

  const isToday = currentDate.isSame(moment(), "day");
  const isCurrentWeek = currentDate.isSame(moment(), "isoWeek");
  const isCurrentMonth = currentDate.isSame(moment(), "month");
  const isCurrent = period === "day" ? isToday : period === "week" ? isCurrentWeek : isCurrentMonth;
  const canGoForward = !isCurrent;

  return (
    <div className="space-y-4" data-testid="worker-time-history">
      {/* Worker selector (admin only) */}
      {isAdmin && members.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {members.map(m => (
            <button
              key={m.user_email}
              onClick={() => setSelectedEmail(m.user_email)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                selectedEmail === m.user_email
                  ? "bg-[#7B2D3F] text-white shadow-sm"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {m.user_name || m.user_email}
            </button>
          ))}
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3" data-testid="time-summary-cards">
        <button
          onClick={() => { setPeriod("day"); setCurrentDate(moment()); }}
          className={`rounded-xl border p-3 text-left transition-all ${
            period === "day" ? "border-[#7B2D3F] bg-[#7B2D3F]/5 shadow-sm" : "border-gray-100 bg-white hover:border-gray-200"
          }`}
          data-testid="period-day-btn"
        >
          <div className="flex items-center gap-1.5 mb-1">
            <Calendar className={`w-3.5 h-3.5 ${period === "day" ? "text-[#7B2D3F]" : "text-gray-400"}`} />
            <span className={`text-xs font-semibold ${period === "day" ? "text-[#7B2D3F]" : "text-gray-500"}`}>Avui</span>
          </div>
          <p className="text-lg font-bold text-gray-900">{minutesToHHMM(todaySummary.mins)}</p>
          {todaySummary.pause > 0 && <p className="text-[10px] text-amber-600">Pausa: {minutesToHHMM(todaySummary.pause)}</p>}
        </button>

        <button
          onClick={() => { setPeriod("week"); setCurrentDate(moment()); }}
          className={`rounded-xl border p-3 text-left transition-all ${
            period === "week" ? "border-[#7B2D3F] bg-[#7B2D3F]/5 shadow-sm" : "border-gray-100 bg-white hover:border-gray-200"
          }`}
          data-testid="period-week-btn"
        >
          <div className="flex items-center gap-1.5 mb-1">
            <CalendarDays className={`w-3.5 h-3.5 ${period === "week" ? "text-[#7B2D3F]" : "text-gray-400"}`} />
            <span className={`text-xs font-semibold ${period === "week" ? "text-[#7B2D3F]" : "text-gray-500"}`}>Setmana</span>
          </div>
          <p className="text-lg font-bold text-gray-900">{minutesToHHMM(weekSummary.mins)}</p>
          <p className="text-[10px] text-gray-400">{weekSummary.days} dies</p>
        </button>

        <button
          onClick={() => { setPeriod("month"); setCurrentDate(moment()); }}
          className={`rounded-xl border p-3 text-left transition-all ${
            period === "month" ? "border-[#7B2D3F] bg-[#7B2D3F]/5 shadow-sm" : "border-gray-100 bg-white hover:border-gray-200"
          }`}
          data-testid="period-month-btn"
        >
          <div className="flex items-center gap-1.5 mb-1">
            <CalendarRange className={`w-3.5 h-3.5 ${period === "month" ? "text-[#7B2D3F]" : "text-gray-400"}`} />
            <span className={`text-xs font-semibold ${period === "month" ? "text-[#7B2D3F]" : "text-gray-500"}`}>Mes</span>
          </div>
          <p className="text-lg font-bold text-gray-900">{minutesToHHMM(monthSummary.mins)}</p>
          <p className="text-[10px] text-gray-400">{monthSummary.days} dies</p>
        </button>
      </div>

      {/* Period navigator */}
      <div className="flex items-center justify-between bg-white rounded-xl border border-gray-100 shadow-sm p-3">
        <div className="flex items-center gap-1">
          <button onClick={goBack} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500" data-testid="period-prev-btn">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm font-semibold text-gray-800 capitalize min-w-[160px] text-center">
            {periodLabel}
          </span>
          <button onClick={goForward} disabled={!canGoForward} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 disabled:opacity-30" data-testid="period-next-btn">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center gap-3">
          {!isCurrent && (
            <button onClick={goToday} className="text-xs text-[#7B2D3F] font-medium hover:underline" data-testid="go-today-btn">
              Avui
            </button>
          )}
          <div className="flex items-center gap-3 text-sm">
            <div className="text-center">
              <p className="font-bold text-gray-900">{minutesToHHMM(totalMins)}</p>
              <p className="text-[10px] text-gray-400">Total</p>
            </div>
            {period !== "day" && (
              <div className="text-center">
                <p className="font-bold text-gray-900">{workDays}</p>
                <p className="text-[10px] text-gray-400">Dies</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Weekly summary bar (only in week view) */}
      {period === "week" && !loading && (
        <div className="grid grid-cols-7 gap-1" data-testid="weekly-bar">
          {Array.from({ length: 7 }, (_, i) => {
            const day = currentDate.clone().startOf("isoWeek").add(i, "days");
            const dayStr = day.format("YYYY-MM-DD");
            const dayRecs = byDate[dayStr] || [];
            const worked = calcWorkedMinutes(dayRecs);
            const isToday = day.isSame(moment(), "day");
            const maxHours = 10;
            const barHeight = Math.min(100, (worked / (maxHours * 60)) * 100);
            return (
              <div key={`item-${i}`} className={`flex flex-col items-center gap-1 p-2 rounded-lg ${isToday ? "bg-[#7B2D3F]/5 border border-[#7B2D3F]/20" : "bg-gray-50"}`}>
                <span className={`text-[10px] font-medium uppercase ${isToday ? "text-[#7B2D3F]" : "text-gray-400"}`}>
                  {day.format("dd")}
                </span>
                <div className="w-full h-12 bg-gray-200/50 rounded-md relative overflow-hidden">
                  <div
                    className="absolute bottom-0 w-full rounded-md transition-all"
                    style={{
                      height: `${barHeight}%`,
                      backgroundColor: worked > 0 ? '#7B2D3F' : 'transparent',
                      opacity: worked > 0 ? (isToday ? 1 : 0.6) : 0
                    }}
                  />
                </div>
                <span className={`text-[10px] font-bold ${worked > 0 ? "text-gray-700" : "text-gray-300"}`}>
                  {worked > 0 ? minutesToHHMM(worked) : "-"}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Records list */}
      {loading ? (
        <div className="text-center py-8 text-gray-400 text-sm">Carregant historial...</div>
      ) : Object.keys(byDate).length === 0 ? (
        <div className="text-center py-12 text-gray-300">
          <Clock className="w-10 h-10 mx-auto mb-2" />
          <p className="text-sm">Sense registres per aquest {period === "day" ? "dia" : period === "week" ? "setmana" : "mes"}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {Object.keys(byDate).sort((a, b) => b.localeCompare(a)).map(date => (
            <DayRecordsList key={date} dayRecs={byDate[date]} date={date} />
          ))}
        </div>
      )}
    </div>
  );
}
